import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useDialog } from "components/Actions/ActionBar/hooks";
import UploadFile, {
    useFileTypes
} from "components/Actions/dialogs/Files/UploadFile";
import { FILE_ACTIONS } from "components/Actions/types";
import QueryDropdown from "components/QueryDropdown";
import { TELTONIKA_FILES_COMPANY } from "shared";
import { usePermissions } from "shared/hooks/usePermissions";
import { DROPDOWN_SIZES, ENDPOINTS, ID_TYPE } from "shared/types/enums";

import { SingleFileProps } from "./types";

const SingleFile = ({
    type,
    payloadId,
    value,
    company,
    isLoading,
    hasUploadedFile,
    additionalParams,
    customFileLabel,
    customResource,
    onChange,
    onUpload
}: SingleFileProps) => {
    const { t } = useTranslation();
    const { types, isAllowedToUploadFile } = useFileTypes();
    const { isOpenDialog, openDialog, closeDialog } = useDialog();

    const {
        isReadFileNotAllowed,
        isCreateFileNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const selectedFileType = types.find(fileType => fileType.value === type);

    const getMissingFileTooltipTitle = () => {
        if (isReadFileNotAllowed) {
            return getNoPermissionTooltipTitle(PERMISSIONS.FileRead);
        }

        const fileName = customFileLabel ?? type;

        const tooltipTitleMain = t("Dialog##no dropdown files##main", {
            fileName
        });

        if (isCreateFileNotAllowed) {
            const secondSentence = t(
                "Dialog##no dropdown files##description no permission create file",
                { fileName }
            );

            return `${tooltipTitleMain} ${secondSentence}`;
        }

        const tooltipTitleDescription = t(
            "Dialog##no dropdown files##description",
            {
                fileName
            }
        );

        const secondSentence = selectedFileType?.isAllowedToRender
            ? tooltipTitleDescription
            : "";

        return `${tooltipTitleMain} ${secondSentence}`;
    };

    const [isUploadDialogVisible, setIsUploadDialogVisible] = useState(false);

    const queryFilters = additionalParams ? `&${additionalParams}` : "";

    const tooltipTitle = isUploadDialogVisible
        ? ""
        : getMissingFileTooltipTitle();

    const isAllowedToUploadFileValue = isAllowedToUploadFile(type);

    return (
        <>
            <QueryDropdown
                data-testid={`${payloadId}-query-dropdown`}
                fullWidth
                noOptionsTooltip={{
                    title: tooltipTitle,
                    fileType: type,
                    hasUploadedFile,
                    ...(isAllowedToUploadFileValue
                        ? {
                              buttonText: t("Button##upload file"),
                              onButtonClick: () =>
                                  openDialog(FILE_ACTIONS.UploadFiles)
                          }
                        : {})
                }}
                idType={ID_TYPE.Id}
                size={DROPDOWN_SIZES.Medium}
                labelLeft={t(`General##${customFileLabel ?? type}`)}
                value={value}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
                resource={
                    isReadFileNotAllowed
                        ? ""
                        : `${ENDPOINTS.Files}?type=${customResource ?? type}&company_id=${company.id},${TELTONIKA_FILES_COMPANY}${queryFilters}`
                }
                readOnly={isLoading}
                change={(_, fileValue) => onChange(payloadId, fileValue)}
            />

            <UploadFile
                isOpen={isOpenDialog[FILE_ACTIONS.UploadFiles]}
                close={closeDialog}
                selectedType={selectedFileType}
                customCompanies={[company]}
                onEnter={() => setIsUploadDialogVisible(true)}
                onExited={() => setIsUploadDialogVisible(false)}
                successCallback={() => onUpload(type)}
            />
        </>
    );
};

export default SingleFile;
